import {
  CreateExhibitionBulkItemResult,
  CreateExhibitionBulkResult,
  SimpleExhibitionData,
} from '../../../shared/dto/EntityRepository';
import {
  CreateExhibitionBulkReq,
  CreateExhibitionReq,
  ResolveExhibitionConflictsReq,
  UpdateExhibitionReq,
} from '../../../shared/dto/ReqDtoRepository';
import { useApiWrapper } from '../../../shared/hooks/useApi/apiWrapper';
import { useTempAlertStore } from '../../../shared/store/portal/tempAlertStore';
import {
  buildExhibitionConflictRetryReq,
  ExhibitionArtistResolutionSelection,
} from '../../../shared/util/exhibitionBulk';
import {
  emptyExhibition,
  normalizeExhibitionDetail,
  toExhibitionDetailPreview,
} from '../../component/exhibitions/exhibitionDraft';
import {
  createExhibition,
  createExhibitionBulk,
  deleteExhibition,
  getExhibition,
  getExhibitionList,
  resolveExhibitionConflicts,
  updateExhibition,
} from '../api/adminApi';
import { useAdminExhibitionStore } from '../store/adminExhibitionStore';

let latestExhibitionListRequestId = 0;
let latestExhibitionDetailRequestId = 0;

const invalidateExhibitionListRequests = () => {
  latestExhibitionListRequestId += 1;
};

const normalizeExhibitionSummary = (
  exhibition: SimpleExhibitionData,
): SimpleExhibitionData => ({
  ...exhibition,
  id: Number(exhibition.id),
});

const getExhibitionListPage = async (page: number, size: number) => {
  let response = await getExhibitionList(page, size);
  const lastAvailablePage = Math.max(1, response.data.totalPages);
  if (page > lastAvailablePage) {
    response = await getExhibitionList(lastAvailablePage, size);
  }
  return response;
};

const applyExhibitionListResponse = (
  response: Awaited<ReturnType<typeof getExhibitionList>>,
) => {
  const content = response.data.content.map(normalizeExhibitionSummary);
  const current = useAdminExhibitionStore.getState();
  current.setExhibitionList(content);
  current.setPagination(response.data.page, response.data.totalPages);
  if (current.multiSelectedExhibitionIds.length > 0) {
    const visibleIds = new Set(content.map((item) => Number(item.id)));
    const selectedIds = current.multiSelectedExhibitionIds.filter((id) => visibleIds.has(id));
    if (selectedIds.length > 1) {
      current.setMultiExhibitionSelection(selectedIds);
    } else {
      current.clearExhibitionSelection();
    }
  }
  return content;
};

const toBulkResult = (
  results: CreateExhibitionBulkItemResult[],
): CreateExhibitionBulkResult => {
  const sortedResults = [...results].sort((left, right) =>
    left.rowNumber - right.rowNumber
  );
  return {
    totalCount: sortedResults.length,
    createdCount: sortedResults.filter((item) => item.status === 'CREATED').length,
    duplicateCount: sortedResults.filter(
      (item) => item.status === 'SKIPPED_DUPLICATE',
    ).length,
    conflictCount: sortedResults.filter((item) => item.status === 'CONFLICT').length,
    invalidCount: sortedResults.filter((item) => item.status === 'INVALID').length,
    results: sortedResults,
  };
};

const mergeBulkResults = (
  current: CreateExhibitionBulkItemResult[],
  replacements: CreateExhibitionBulkItemResult[],
): CreateExhibitionBulkItemResult[] => {
  const replacementByRow = new Map(
    replacements.map((item) => [item.rowNumber, item]),
  );
  const merged = current.map((item) =>
    replacementByRow.get(item.rowNumber) ?? item
  );
  const currentRows = new Set(current.map((item) => item.rowNumber));
  replacements.forEach((item) => {
    if (!currentRows.has(item.rowNumber)) merged.push(item);
  });
  return merged;
};

export const useAdminExhibition = () => {
  const store = useAdminExhibitionStore();
  const withApiHandler = useApiWrapper();
  const setUpdatedTempAlert = useTempAlertStore(
    (state) => state.setUpdatedTempAlert,
  );
  const setDeletedTempAlert = useTempAlertStore(
    (state) => state.setDeletedTempAlert,
  );

  const load = async (
    page = useAdminExhibitionStore.getState().page,
    size = Number(useAdminExhibitionStore.getState().pageSize),
  ): Promise<void> => {
    const requestId = ++latestExhibitionListRequestId;
    await withApiHandler(async () => {
      let response: Awaited<ReturnType<typeof getExhibitionList>>;
      try {
        response = await getExhibitionListPage(page, size);
      } catch (error) {
        if (requestId !== latestExhibitionListRequestId) return;
        throw error;
      }
      if (requestId !== latestExhibitionListRequestId) return;
      applyExhibitionListResponse(response);
    }, ['get-exhibition-list', page, size]);
  };

  const isCurrentExhibition = (exhibitionId: number, requestId: number) => {
    const current = useAdminExhibitionStore.getState();
    return requestId === latestExhibitionDetailRequestId
      && current.multiSelectedExhibitionIds.length === 0
      && current.activeExhibition?.id === exhibitionId;
  };

  const selectExhibition = async (
    exhibition: SimpleExhibitionData,
  ): Promise<void> => {
    const normalizedId = Number(exhibition.id);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;

    const requestId = ++latestExhibitionDetailRequestId;
    const current = useAdminExhibitionStore.getState();
    current.setHasChanged(false);
    current.setActiveExhibition(toExhibitionDetailPreview(exhibition));

    await withApiHandler(async () => {
      let response: Awaited<ReturnType<typeof getExhibition>>;
      try {
        response = await getExhibition(normalizedId);
      } catch (error) {
        if (!isCurrentExhibition(normalizedId, requestId)) return;
        useAdminExhibitionStore.getState().clearExhibitionSelection();
        throw error;
      }
      if (!isCurrentExhibition(normalizedId, requestId)) return;
      useAdminExhibitionStore.getState().setActiveExhibition(
        normalizeExhibitionDetail(response.data),
      );
    }, ['get-exhibition', normalizedId]);
  };

  const selectExhibitionById = async (exhibitionId: number): Promise<void> => {
    const normalizedId = Number(exhibitionId);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) return;

    const fromList = useAdminExhibitionStore.getState().exhibitionList.find(
      (item) => Number(item.id) === normalizedId,
    );
    if (fromList) {
      await selectExhibition(fromList);
      return;
    }

    const requestId = ++latestExhibitionDetailRequestId;
    const current = useAdminExhibitionStore.getState();
    current.setHasChanged(false);
    current.setActiveExhibition({ ...emptyExhibition(), id: normalizedId });

    await withApiHandler(async () => {
      let response: Awaited<ReturnType<typeof getExhibition>>;
      try {
        response = await getExhibition(normalizedId);
      } catch (error) {
        if (!isCurrentExhibition(normalizedId, requestId)) return;
        useAdminExhibitionStore.getState().clearExhibitionSelection();
        throw error;
      }
      if (!isCurrentExhibition(normalizedId, requestId)) return;
      useAdminExhibitionStore.getState().setActiveExhibition(
        normalizeExhibitionDetail(response.data),
      );
    }, ['get-exhibition', normalizedId]);
  };

  const selectExhibitions = (exhibitions: SimpleExhibitionData[]): void => {
    const uniqueExhibitions = exhibitions.filter((item, index, list) => {
      const id = Number(item.id);
      return Number.isFinite(id)
        && id > 0
        && list.findIndex((candidate) => Number(candidate.id) === id) === index;
    });
    latestExhibitionDetailRequestId += 1;
    if (uniqueExhibitions.length === 0) {
      useAdminExhibitionStore.getState().clearExhibitionSelection();
    } else if (uniqueExhibitions.length === 1) {
      void selectExhibition(uniqueExhibitions[0]);
    } else {
      useAdminExhibitionStore.getState().setMultiExhibitionSelection(
        uniqueExhibitions.map((item) => Number(item.id)),
      );
    }
  };

  const clearExhibitionSelection = () => {
    latestExhibitionDetailRequestId += 1;
    useAdminExhibitionStore.getState().clearExhibitionSelection();
  };

  const create = async (data: CreateExhibitionReq): Promise<boolean> => {
    const created = await withApiHandler(async () => {
      const response = await createExhibition(data);
      const detail = normalizeExhibitionDetail(response.data);
      invalidateExhibitionListRequests();
      const current = useAdminExhibitionStore.getState();
      current.setHasChanged(false);
      current.setActiveExhibition(detail);
      current.setBulkResult(null);
      setUpdatedTempAlert();
      return detail;
    }, ['create-exhibition']);
    if (!created) return false;

    const { page, pageSize } = useAdminExhibitionStore.getState();
    await load(page, Number(pageSize));
    return true;
  };

  const update = async (
    exhibitionId: number,
    data: UpdateExhibitionReq,
  ): Promise<boolean> => {
    const updated = await withApiHandler(async () => {
      const response = await updateExhibition(exhibitionId, data);
      const detail = normalizeExhibitionDetail(response.data);
      invalidateExhibitionListRequests();
      useAdminExhibitionStore.getState().setHasChanged(false);
      useAdminExhibitionStore.getState().setActiveExhibition(detail);
      setUpdatedTempAlert();
      return detail;
    }, ['update-exhibition', exhibitionId]);
    if (!updated) return false;

    const { page, pageSize } = useAdminExhibitionStore.getState();
    await load(page, Number(pageSize));
    return true;
  };

  const remove = async (exhibitionId: number): Promise<boolean> => {
    const current = useAdminExhibitionStore.getState();
    const targetPage = current.exhibitionList.length === 1 && current.page > 1
      ? current.page - 1
      : current.page;
    const pageSize = Number(current.pageSize);

    const removed = await withApiHandler(async () => {
      await deleteExhibition(exhibitionId);
      invalidateExhibitionListRequests();
      latestExhibitionDetailRequestId += 1;
      useAdminExhibitionStore.getState().clearExhibitionSelection();
      setDeletedTempAlert();
      return true;
    }, ['delete-exhibition', exhibitionId]);
    if (!removed) return false;

    await load(targetPage, pageSize);
    return true;
  };

  const removeSelectedExhibitions = async (): Promise<boolean> => {
    const current = useAdminExhibitionStore.getState();
    const exhibitionIds = [...current.multiSelectedExhibitionIds];
    if (exhibitionIds.length < 2) return false;

    const page = current.page;
    const size = Number(current.pageSize);
    const removed = await withApiHandler(async () => {
      const results = await Promise.allSettled(
        exhibitionIds.map((id) => deleteExhibition(id)),
      );
      const deletedIds = exhibitionIds.filter(
        (_, index) => results[index].status === 'fulfilled',
      );
      const failedIds = exhibitionIds.filter(
        (_, index) => results[index].status === 'rejected',
      );

      if (deletedIds.length > 0) {
        invalidateExhibitionListRequests();
        latestExhibitionDetailRequestId += 1;
        const latest = useAdminExhibitionStore.getState();
        const deletedIdSet = new Set(deletedIds);
        latest.setExhibitionList(
          latest.exhibitionList.filter((item) => !deletedIdSet.has(Number(item.id))),
        );
        if (failedIds.length > 1) {
          latest.setMultiExhibitionSelection(failedIds);
        } else if (failedIds.length === 1) {
          const remaining = latest.exhibitionList.find(
            (item) => Number(item.id) === failedIds[0],
          );
          latest.setActiveExhibition(
            remaining
              ? toExhibitionDetailPreview(remaining)
              : { ...emptyExhibition(), id: failedIds[0] },
          );
        } else {
          latest.clearExhibitionSelection();
        }

        const targetPage = deletedIds.length >= current.exhibitionList.length && page > 1
          ? page - 1
          : page;
        await load(targetPage, size);
        if (failedIds.length === 1) {
          await selectExhibitionById(failedIds[0]);
        }
      }

      const failure = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (failure) throw failure.reason;
      return true;
    }, ['delete-selected-exhibitions', exhibitionIds], { maxRetries: 1 });
    if (!removed) return false;

    setDeletedTempAlert();
    return true;
  };

  const createBulk = async (
    data: CreateExhibitionBulkReq,
    invalidResults: CreateExhibitionBulkItemResult[] = [],
  ): Promise<boolean> => {
    if (data.exhibitionList.length === 0) {
      clearExhibitionSelection();
      const current = useAdminExhibitionStore.getState();
      current.setBulkItems([]);
      current.setBulkResult(toBulkResult(invalidResults));
      return true;
    }

    const response = await withApiHandler(
      () => createExhibitionBulk(data),
      ['create-exhibition-bulk'],
    );
    if (!response) return false;

    invalidateExhibitionListRequests();
    clearExhibitionSelection();
    const current = useAdminExhibitionStore.getState();
    current.setBulkItems(data.exhibitionList);
    current.setBulkResult(toBulkResult([
      ...response.data.results,
      ...invalidResults,
    ]));
    await load(1, Number(current.pageSize));
    return true;
  };

  const retryBulkConflict = async (
    rowNumber: number,
    selections: ExhibitionArtistResolutionSelection[],
  ): Promise<boolean> => {
    const current = useAdminExhibitionStore.getState();
    const sourceItem = current.bulkItems.find(
      (item) => item.rowNumber === rowNumber,
    );
    if (!sourceItem) return false;

    let retryRequest: CreateExhibitionBulkReq;
    try {
      retryRequest = buildExhibitionConflictRetryReq(sourceItem, selections);
    } catch {
      return false;
    }

    const response = await withApiHandler(
      () => createExhibitionBulk(retryRequest),
      ['retry-exhibition-conflict', rowNumber, selections],
    );
    if (!response || response.data.results.length === 0) return false;

    const latest = useAdminExhibitionStore.getState();
    const resolvedItem = retryRequest.exhibitionList[0];
    latest.setBulkItems(latest.bulkItems.map((item) =>
      item.rowNumber === rowNumber ? resolvedItem : item
    ));
    latest.setBulkResult(toBulkResult(mergeBulkResults(
      latest.bulkResult?.results ?? [],
      response.data.results,
    )));

    if (response.data.results.some((item) => item.status === 'CREATED')) {
      invalidateExhibitionListRequests();
      await load(latest.page, Number(latest.pageSize));
    }
    return true;
  };

  const resolveBulkExhibitionConflicts = async (
    request: ResolveExhibitionConflictsReq,
  ): Promise<boolean> => {
    const response = await withApiHandler(
      () => resolveExhibitionConflicts(request),
      ['resolve-exhibition-conflicts'],
    );
    if (!response) return false;

    const updatedIds = new Set(
      response.data.results
        .filter((item) => item.status === 'UPDATED')
        .map((item) => item.exhibitionId),
    );
    const invalidMessageById = new Map(
      response.data.results
        .filter((item) => item.status === 'INVALID')
        .map((item) => [item.exhibitionId, item.message]),
    );

    const latest = useAdminExhibitionStore.getState();
    latest.setBulkResult(toBulkResult(
      (latest.bulkResult?.results ?? []).map((item) => {
        if (item.exhibitionId == null || !item.exhibitionConflict) return item;
        if (updatedIds.has(item.exhibitionId)) {
          return {
            ...item,
            status: 'UPDATED',
            message: null,
            exhibitionConflict: undefined,
          };
        }
        if (invalidMessageById.has(item.exhibitionId)) {
          return {
            ...item,
            message: invalidMessageById.get(item.exhibitionId) ?? item.message,
          };
        }
        return item;
      }),
    ));

    if (updatedIds.size > 0) {
      invalidateExhibitionListRequests();
      await load(latest.page, Number(latest.pageSize));
    }
    return true;
  };

  return {
    ...store,
    load,
    selectExhibition,
    selectExhibitionById,
    selectExhibitions,
    clearExhibitionSelection,
    create,
    update,
    remove,
    removeSelectedExhibitions,
    createBulk,
    retryBulkConflict,
    resolveBulkExhibitionConflicts,
  };
};
